<script setup lang="ts">
import Dialog from 'primevue/dialog';

// Presentational one-shot announcement for the knowledge base (RAG) feature.
// The parent owns *whether* it opens: App.vue shows it once per user, and only
// when the platform-wide Knowledge Base switch is on. Nothing here fetches or
// decides anything.
//
// The hero is inline SVG rather than a PNG (the pattern the two welcome
// dialogs use) so it inherits the design tokens and stays crisp; there is no
// artwork for this feature yet.
//
// SOURCE_TYPES mirrors buildAddSourceMenuItems() in ../kbSourceMenu.ts, label
// and icon both. It is a copy rather than an import because that builder
// wants a command handler per item and this is a picture of the menu, not the
// menu; if a source type is added there, add it here too.
const SOURCE_TYPES = [
  { icon: 'pi-upload', label: 'File upload' },
  { icon: 'pi-globe', label: 'Web crawl' },
  { icon: 'pi-bolt', label: 'API connector' },
  { icon: 'pi-send', label: 'Live API' },
  { icon: 'pi-server', label: 'SFTP' },
  { icon: 'pi-file-import', label: 'JSON upload' },
];

defineProps<{
  visible: boolean;
  docsUrl: string;
}>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
}>();

function close() {
  emit('update:visible', false);
}
</script>

<template>
  <Dialog
    :visible="visible"
    modal
    :show-header="false"
    :dismissable-mask="true"
    :pt="{
      root: { class: 'rag-announce-root' },
      content: { class: 'rag-announce-content' },
    }"
    :style="{ width: '480px', maxWidth: 'calc(100vw - 32px)' }"
    aria-labelledby="rag-announce-title"
    @update:visible="(v) => emit('update:visible', v)"
  >
    <button class="close-x" aria-label="Close" @click="close">
      <i class="pi pi-times" />
    </button>

    <div class="hero">
      <svg viewBox="0 0 480 158" class="hero-art" role="img" aria-hidden="true" focusable="false">
        <!-- Your sources -->
        <g class="art-doc">
          <rect x="54" y="44" width="52" height="64" rx="6" />
          <rect x="66" y="36" width="52" height="64" rx="6" />
          <rect x="78" y="28" width="52" height="64" rx="6" />
        </g>
        <g class="art-line">
          <rect x="88" y="42" width="32" height="4" rx="2" />
          <rect x="88" y="54" width="26" height="4" rx="2" />
          <rect x="88" y="66" width="32" height="4" rx="2" />
          <rect x="88" y="78" width="18" height="4" rx="2" />
        </g>
        <text x="92" y="132" class="art-label" text-anchor="middle">Your sources</text>

        <!-- Documents -> knowledge base -->
        <path d="M142 64 H198" class="art-flow" />
        <circle cx="170" cy="64" r="3" class="art-dot" />

        <!-- Knowledge base -->
        <g class="art-kb">
          <ellipse cx="240" cy="42" rx="38" ry="12" />
          <path d="M202 42 V90 a38 12 0 0 0 76 0 V42" />
          <ellipse cx="240" cy="66" rx="38" ry="12" class="art-kb-band" />
        </g>
        <text x="240" y="132" class="art-label" text-anchor="middle">Knowledge base</text>

        <!-- Knowledge base -> grounded answer -->
        <path d="M282 64 H338" class="art-flow" />
        <circle cx="310" cy="64" r="3" class="art-dot" />

        <!-- Matching passages -->
        <g class="art-answer">
          <rect x="342" y="32" width="92" height="64" rx="10" />
          <rect x="356" y="50" width="58" height="5" rx="2.5" class="art-answer-line" />
          <rect x="356" y="63" width="44" height="5" rx="2.5" class="art-answer-line" />
          <rect x="356" y="76" width="52" height="5" rx="2.5" class="art-answer-line" />
        </g>
        <text x="388" y="132" class="art-label" text-anchor="middle">Matching passages</text>
      </svg>
    </div>

    <div class="body">
      <span class="pill">New</span>
      <h2 id="rag-announce-title" class="title">Knowledge bases are here</h2>
      <p class="desc pb-4">
        A model only knows what it was trained on. Put your own documents in a knowledge base and
        Parley will search them for you, returning the passages that match a question. You pass
        those to the model yourself, so the answer rests on your material instead of its memory.
      </p>

      <div class="sources">
        <span class="sources-label">Connect content from</span>
        <ul class="chips">
          <li v-for="source in SOURCE_TYPES" :key="source.label" class="chip">
            <i :class="['pi', source.icon]" aria-hidden="true" />
            {{ source.label }}
          </li>
        </ul>
      </div>

      <ul class="points">
        <li>
          <i class="pi pi-power-off" aria-hidden="true" />
          <span>
            Enable the knowledge base on an account's <strong>Knowledge base</strong> tab, then add
            your sources
          </span>
        </li>
        <li>
          <i class="pi pi-building-columns" aria-hidden="true" />
          <span>
            Set a <strong>retrieval scope</strong>: this account's own knowledge base, the shared
            MIT one, or both (the default)
          </span>
        </li>
        <li>
          <i class="pi pi-search" aria-hidden="true" />
          <span>
            Query it from your API client with <code>/v1/rag/retrieve</code>, authenticated with
            your Parley API key
          </span>
        </li>
      </ul>

      <!-- The account-tier retrieval kill switch ships seeded off, so a fully
           built knowledge base can still return nothing. Called out as a note
           rather than a fourth step: it is a prerequisite most readers cannot
           action themselves (KbOversightPanel is super-admin only). -->
      <p class="note">
        <i class="pi pi-info-circle" aria-hidden="true" />
        <span>
          Account retrieval has to be switched on for the platform before queries return anything.
        </span>
      </p>

      <a class="cta" :href="docsUrl" target="_blank" rel="noopener" @click="close">
        Read the knowledge base docs
        <i class="pi pi-external-link" aria-hidden="true" />
      </a>

      <button class="dismiss" type="button" @click="close">Maybe later</button>
    </div>
  </Dialog>
</template>

<style scoped>
:global(.rag-announce-root) {
  border-radius: var(--radius-lg);
  overflow: hidden;
}

:global(.rag-announce-content) {
  padding: 0 !important;
  background: var(--surface);
  display: flex;
  flex-direction: column;
  /* close-x is positioned against this container. */
  position: relative;
}

.close-x {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid var(--border-2);
  background: var(--surface);
  color: var(--ink-2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.close-x:hover {
  background: var(--surface-hover-alt);
}

.close-x .pi {
  font-size: 12px;
}

.hero {
  background: linear-gradient(160deg, var(--accent-soft), var(--surface-2));
  border-bottom: 1px solid var(--border);
  padding: 8px 0 0;
}

.hero-art {
  display: block;
  width: 100%;
  height: auto;
  user-select: none;
  pointer-events: none;
}

.art-doc rect {
  fill: var(--surface);
  stroke: var(--border-strong);
  stroke-width: 1.5;
}

.art-line rect {
  fill: var(--ink-4);
}

.art-flow {
  stroke: var(--border-strong);
  stroke-width: 1.5;
  stroke-dasharray: 4 4;
  fill: none;
}

.art-dot {
  fill: var(--accent);
}

.art-kb ellipse,
.art-kb path {
  fill: var(--accent-tint);
  stroke: var(--accent);
  stroke-width: 1.5;
}

.art-kb-band {
  fill: none;
}

.art-answer rect:first-child {
  fill: var(--surface);
  stroke: var(--border-strong);
  stroke-width: 1.5;
}

.art-answer-line {
  fill: var(--accent);
  opacity: 0.55;
}

.art-label {
  fill: var(--ink-3);
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: 600;
}

.body {
  padding: 22px 28px 26px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pill {
  align-self: flex-start;
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--accent-tint);
  color: var(--accent);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--ink);
}

.desc {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: var(--ink-2);
}

.sources {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 2px;
}

.sources-label {
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ink-3);
}

.chips {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border: 1px solid var(--border-2);
  border-radius: 999px;
  background: var(--surface-2);
  font-size: 12.5px;
  color: var(--ink-2);
  white-space: nowrap;
}

.chip .pi {
  font-size: 11.5px;
  color: var(--accent);
}

.points {
  margin: 4px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.points li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--ink-2);
}

.points .pi {
  margin-top: 2px;
  font-size: 13px;
  color: var(--accent);
}

.points strong {
  color: var(--ink);
  font-weight: 600;
}

.points code {
  font-family: var(--font-mono);
  font-size: 12px;
  padding: 1px 5px;
  border-radius: var(--radius-sm);
  background: var(--surface-2);
  color: var(--ink);
}

.note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 2px 0 0;
  padding: 10px 12px;
  border: 1px solid var(--border-2);
  border-radius: var(--radius-md);
  background: var(--surface-2);
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--ink-2);
}

.note .pi {
  margin-top: 1px;
  font-size: 12.5px;
  color: var(--ink-3);
}

.note strong {
  color: var(--ink);
  font-weight: 600;
}

.cta {
  margin-top: 10px;
  width: 100%;
  padding: 13px 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--ink);
  color: var(--surface);
  border: none;
  border-radius: 999px;
  font-size: 14.5px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.cta:hover {
  opacity: 0.92;
}

.cta .pi {
  font-size: 12px;
}

.dismiss {
  align-self: center;
  border: none;
  background: transparent;
  color: var(--ink-3);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  padding: 2px 6px;
}

.dismiss:hover {
  color: var(--ink);
}
</style>
